// src/contexts/ProjectsContext.js
import { createContext, useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useUser } from './UserContext'
import { isAdminRole } from 'utils/roles'

const ProjectsContext = createContext()

// ID del proyecto predeterminado (solo para administradores)
const DEFAULT_PROJECT_ID = 'proj_y1b3cEVFRbiYETBcLC4LWRR8'

export function ProjectsProvider({ children, defaultProjectId = DEFAULT_PROJECT_ID }) {
  const { user } = useUser() // Obtenemos el usuario actual

  // Estado para el proyecto seleccionado
  const [selectedProjectId, setSelectedProjectId] = useState('')
  
  // Nuevo estado para mantener información del proyecto actual
  const [currentProjectInfo, setCurrentProjectInfo] = useState(null)

  // Efecto para inicializar el proyecto seleccionado basado en el usuario
  useEffect(() => {
    if (user) {
      // Si el usuario tiene rol 2 o 3 y tiene project_id, forzar ese proyecto
      if ((user.role === 2 || user.role === 3) && user.project_id) {
        console.log('Setting restricted project:', user.project_id);
        setSelectedProjectId(user.project_id);
        localStorage.setItem('selectedProjectId', user.project_id);
        
        // Guardar información del proyecto actual
        setCurrentProjectInfo({
          id: user.project_id,
          name: user.project_name,
          internal_id: user.project_internal_id
        });
      } 
      // Si es admin (rol 1), cargar desde localStorage o usar el default
      else if (isAdminRole(user.role)) {
        const savedProjectId = localStorage.getItem('selectedProjectId');
        setSelectedProjectId(savedProjectId || defaultProjectId);
      }
    } else {
      // Si no hay usuario, usar localStorage o el default
      const savedProjectId = localStorage.getItem('selectedProjectId');
      setSelectedProjectId(savedProjectId || defaultProjectId);
    }
  }, [user, defaultProjectId]);

  // Función segura para cambiar el proyecto
  const safeSetSelectedProjectId = (newProjectId) => {
    // Verificar si el usuario puede cambiar el proyecto
    if (!user || isAdminRole(user.role)) {
      // Administradores pueden cambiar libremente
      setSelectedProjectId(newProjectId);
      localStorage.setItem('selectedProjectId', newProjectId);
    } else if ((user.role === 2 || user.role === 3) && user.project_id) {
      // Usuarios con roles 2 o 3 solo pueden usar su proyecto asignado
      if (newProjectId !== user.project_id) {
        console.warn('Usuario no tiene permisos para cambiar de proyecto');
        // Forzar de vuelta a su proyecto asignado
        setSelectedProjectId(user.project_id);
      }
    }
  };

  // Función para determinar si el proyecto actual es editable
  const isProjectEditable = () => {
    // Solo administradores (rol 1) pueden editar proyectos
    return user && isAdminRole(user.role);
  };

  // Función para filtrar usuarios según el proyecto actual
  const filterUsersByCurrentProject = (usersList) => {
    if (!selectedProjectId || !user) return usersList;
    
    // Administradores pueden ver todos los usuarios o filtrar por proyecto seleccionado
    if (isAdminRole(user.role)) {
      // Si hay un proyecto seleccionado, filtrar por ese proyecto
      return usersList.filter(u => !u.project_id || u.project_id === selectedProjectId);
    }
    
    // Editores (rol 2) solo pueden ver usuarios de su proyecto asignado
    if (user.role === 2 && user.project_id) {
      return usersList.filter(u => u.project_id === user.project_id);
    }
    
    // Por defecto, devolver todos los usuarios
    return usersList;
  };

  return (
    <ProjectsContext.Provider value={{ 
      selectedProjectId, 
      setSelectedProjectId: safeSetSelectedProjectId,
      resetToDefaultProject: () => {
        if (user && (user.role === 2 || user.role === 3) && user.project_id) {
          setSelectedProjectId(user.project_id);
        } else {
          setSelectedProjectId(defaultProjectId);
        }
      },
      // Nuevas propiedades
      currentProjectInfo,
      isProjectEditable,
      filterUsersByCurrentProject
    }}>
      {children}
    </ProjectsContext.Provider>
  )
}

// Hook personalizado para usar el contexto de proyectos
export function useProjects() {
  return useContext(ProjectsContext)
}

// Definir PropTypes
ProjectsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultProjectId: PropTypes.string
}